// panels/SaveAsNewModal.jsx
import React, { useState, useCallback, useEffect } from 'react';
import { manufacturingAPI } from '../../../services/api';
import './SaveAsNewModal.css';

const ITEM_TYPES = [
  { value: 'raw_material', label: 'Raw Material' },
  { value: 'manufactured', label: 'Manufactured Part' },
  { value: 'assembly', label: 'Assembly' },
  { value: 'finished_good', label: 'Finished Good' },
];

const SaveAsNewModal = ({ 
  onClose, 
  onConfirm, 
  currentTopLevel,
  existingPendingCodes = [] 
}) => {
  const [itemCode, setItemCode] = useState('');
  const [itemName, setItemName] = useState('');
  const [description, setDescription] = useState('');
  const [itemType, setItemType] = useState('finished_good');
  const [isPhantom, setIsPhantom] = useState(false);
  
  const [isValidating, setIsValidating] = useState(false);
  const [codeError, setCodeError] = useState('');
  const [codeValid, setCodeValid] = useState(false);

  // Pre-fill with current top level data + COPY suffix
  useEffect(() => {
    if (currentTopLevel) {
      const suggestedCode = `${currentTopLevel.itemCode}-COPY`;
      setItemCode(suggestedCode);
      setItemName(`${currentTopLevel.itemName} (Copy)`);
      setDescription(currentTopLevel.description || '');
      setItemType(currentTopLevel.itemType || 'finished_good');
      setIsPhantom(currentTopLevel.isPhantom || false);
    }
  }, [currentTopLevel]);

  // Debounced validation of item code
  useEffect(() => {
    if (!itemCode.trim()) {
      setCodeError('');
      setCodeValid(false);
      return;
    }

    const codeUpper = itemCode.trim().toUpperCase();
    
    // Check if same as current top level
    if (currentTopLevel && codeUpper === currentTopLevel.itemCode.toUpperCase()) {
      setCodeError('Code must be different from the original item');
      setCodeValid(false);
      return;
    }

    // Check against pending items
    if (existingPendingCodes.some(c => c.toUpperCase() === codeUpper)) {
      setCodeError('This code is already being used for a new item on the canvas');
      setCodeValid(false);
      return;
    }

    // Debounce API check
    const timer = setTimeout(async () => {
      setIsValidating(true);
      try {
        const response = await manufacturingAPI.items.list({
          search: itemCode.trim(),
          page_size: 10,
        });
        
        const items = response.data.results || response.data;
        const exactMatch = items.find(
          item => item.item_code.toUpperCase() === codeUpper
        );
        
        if (exactMatch) {
          setCodeError(`Item code "${exactMatch.item_code}" already exists in database`);
          setCodeValid(false);
        } else {
          setCodeError('');
          setCodeValid(true);
        }
      } catch (err) {
        console.error('Failed to validate item code:', err);
        setCodeError('');
        setCodeValid(true);
      } finally {
        setIsValidating(false);
      }
    }, 500);

    return () => clearTimeout(timer);
  }, [itemCode, existingPendingCodes, currentTopLevel]);

  const handleSubmit = useCallback((e) => {
    e.preventDefault();
    
    if (!itemCode.trim() || !itemName.trim()) {
      return;
    }

    if (codeError || !codeValid) {
      return;
    }

    const newTopLevelItem = {
      id: `pending-${Date.now()}`,
      item_code: itemCode.trim().toUpperCase(),
      name: itemName.trim(),
      description: description.trim(),
      item_type: itemType,
      is_phantom: isPhantom,
      isPending: true,
    };

    onConfirm(newTopLevelItem);
  }, [itemCode, itemName, description, itemType, isPhantom, codeError, codeValid, onConfirm]);

  const isFormValid = itemCode.trim() && itemName.trim() && codeValid && !codeError && !isValidating;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="save-as-new-modal" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="modal-header">
          <h2>Save as New Product</h2>
          <button className="close-btn" onClick={onClose}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="18" y1="6" x2="6" y2="18"/>
              <line x1="6" y1="6" x2="18" y2="18"/>
            </svg>
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit}>
          <div className="modal-content">
            {/* Info Banner */}
            <div className="info-banner">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="12" cy="12" r="10"/>
                <line x1="12" y1="16" x2="12" y2="12"/>
                <line x1="12" y1="8" x2="12.01" y2="8"/>
              </svg>
              <span>
                This will create a new top-level item with the BOM structure from your canvas. 
                Child items will be reused unless you change their codes in the Edit panel.
              </span>
            </div>

            {/* Current Item Reference */}
            {currentTopLevel && !currentTopLevel.isPending && (
              <div className="original-item-info">
                <label>Based on:</label>
                <span className="original-code">{currentTopLevel.itemCode}</span>
                <span className="original-name">{currentTopLevel.itemName}</span>
              </div>
            )}

            {/* Item Code */}
            <div className="form-group">
              <label htmlFor="newItemCode">
                New Item Code <span className="required">*</span>
              </label>
              <div className="input-with-status">
                <input
                  id="newItemCode"
                  type="text"
                  value={itemCode}
                  onChange={(e) => setItemCode(e.target.value.toUpperCase())}
                  placeholder="e.g. PRODUCT-NEW"
                  autoFocus
                  className={codeError ? 'error' : codeValid ? 'valid' : ''}
                />
                <div className="input-status">
                  {isValidating && (
                    <span className="status-validating">
                      <span className="mini-spinner"></span>
                    </span>
                  )}
                  {!isValidating && codeValid && (
                    <span className="status-valid">
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <polyline points="20 6 9 17 4 12"/>
                      </svg>
                    </span>
                  )}
                  {!isValidating && codeError && (
                    <span className="status-error">
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <line x1="18" y1="6" x2="6" y2="18"/>
                        <line x1="6" y1="6" x2="18" y2="18"/>
                      </svg>
                    </span>
                  )}
                </div>
              </div>
              {codeError && <p className="field-error">{codeError}</p>}
              {!codeError && codeValid && (
                <p className="field-success">Item code is available</p>
              )}
            </div>

            {/* Item Name */}
            <div className="form-group">
              <label htmlFor="newItemName">
                Item Name <span className="required">*</span>
              </label>
              <input
                id="newItemName"
                type="text"
                value={itemName}
                onChange={(e) => setItemName(e.target.value)}
                placeholder="e.g. New Product Assembly"
              />
            </div>

            {/* Description */}
            <div className="form-group">
              <label htmlFor="newDescription">
                Description
              </label>
              <textarea
                id="newDescription"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Optional description"
                rows={2}
              />
            </div>

            {/* Item Type */}
            <div className="form-group">
              <label htmlFor="newItemType">Item Type</label>
              <select
                id="newItemType"
                value={itemType}
                onChange={(e) => setItemType(e.target.value)}
              >
                {ITEM_TYPES.map(type => (
                  <option key={type.value} value={type.value}>
                    {type.label}
                  </option>
                ))}
              </select>
            </div>

            {/* Is Phantom */}
            <div className="form-group checkbox-group">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={isPhantom}
                  onChange={(e) => setIsPhantom(e.target.checked)}
                />
                <span className="checkbox-text">
                  Phantom Item
                  <span className="checkbox-hint">
                    Components explode through to parent assembly
                  </span>
                </span>
              </label>
            </div>

            {/* What happens next */}
            <div className="next-steps-info">
              <strong>What happens next:</strong>
              <ul>
                <li>A new top-level item will be created with this code</li>
                <li>All BOM relationships will be created pointing to this new item</li>
                <li>Child items will be reused (same item IDs)</li>
                <li>To create new child items, edit their codes in the Edit panel before saving</li>
              </ul>
            </div>
          </div>

          {/* Footer */}
          <div className="modal-footer">
            <button type="button" className="btn-cancel" onClick={onClose}>
              Cancel
            </button>
            <button 
              type="submit" 
              className="btn-confirm"
              disabled={!isFormValid}
            >
              Continue
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default SaveAsNewModal;